import {
  addDisplayName,
  addHandlers,
  addProps,
  addRef,
  addWrapper,
  flowMax,
} from 'ad-hok'
import { branchIfPassesPredicate } from 'ad-hok-utils'
import { compact } from 'lodash/fp'
import React, { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import {
  isBrandFeed,
  isLabelFeed,
  isTagFeed,
  isUserFeed,
} from 'shared/src/api/types'
import {
  addLocation,
  addNavigate,
  addOptionalIntPathParam,
  addOptionalPathParam,
} from 'shared/src/utils/addNavigate'
import { navBackOrHomepage } from 'shared/src/utils/navBack'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { addAuthContext } from 'shared/src/utils/auth'
import {
  addDataLoadingFromPromise,
  hasData,
  isLoading,
} from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'

import backButtonIcon from 'assets/caret-left.svg'
import { ImageButton } from 'components/ImageButton'
import { RodeoAndEditTabs } from 'components/RodeoAndEditTabs'
import { Footer } from 'components/footer'
import { NavBar } from 'components/navbar'
import { addLoadingAndErrorIndicators } from 'utils/addLoadingAndErrorIndicators'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addShowRegisterContext } from 'utils/register'
import {
  RodeoOrEditViewType,
  addRodeoOrEditViewContextProvider,
} from 'utils/rodeoOrEditViewContext'
import { makeCreatorProfileUrl } from 'utils/routes'
import { FLUID_CONTAINER_STYLE } from 'utils/styles'
import { setPageTitle } from 'utils/title'
import { NAVBAR_Z_INDEX } from 'utils/zIndex'
import { ConstrainWidth } from '../../utils/layout'
import { Tabs } from '../menu/Tabs'
import { addFeedContextProvider } from './context'
import { Fluid } from './fluid'
import { TABS_HEIGHT, TABS_TOP } from './layout'
import { Banner } from 'components/banners/Banner'
import { GO2APP_URL } from 'utils/urls'

const useStyles = createUseStyles({
  container: {
    height: '100%',
    ...FLUID_CONTAINER_STYLE,
  },
  header: {
    position: 'relative',
  },
  roundupButton: {
    position: 'absolute',
    top: 16,
    right: 34,
    zIndex: NAVBAR_Z_INDEX,
  },
  tabs: {
    position: 'fixed',
    top: TABS_TOP,
    height: TABS_HEIGHT,
    paddingLeft: 40,
    zIndex: NAVBAR_Z_INDEX,
    minWidth: 320,
    maxWidth: 'calc(428px - 86px)',
    width: 'calc(100vw - 46px)',
  },
  navBarContainer: {
    position: 'relative',
  },
  backButton: {
    position: 'fixed',
    top: 0,
    color: 'white',
    zIndex: NAVBAR_Z_INDEX,
    marginTop: 15,
    marginLeft: 13,
  },
  loading: {
    position: 'relative',
    color: '#ffffff',
    fontSize: 30,
    top: '50%',
  },
  wantMore: {
    scrollSnapAlign: 'end',
  },
})

type InitialFeed = 'featured' | 'following'

interface Props {
  initialFeedId?: InitialFeed
}

export const Feed: FC<Props> = flowMax(
  addDisplayName('Feed'),
  addOptionalPathParam('username'),
  addOptionalIntPathParam('brandId'),
  addOptionalIntPathParam('tagId'),
  addOptionalIntPathParam('labelId'),
  addWrapper((render, { username, brandId, tagId, labelId, initialFeedId }) => (
    <React.Fragment
      key={`${username}-${brandId}-${tagId}-${labelId}-${initialFeedId}`}
    >
      {render()}
    </React.Fragment>
  )),
  addApi,
  addAuthContext,
  addDataLoadingFromPromise(
    'defaultTag',
    ({ api }) => api.tags.default(),
    [],
    () => 'defaultTag'
  ),
  branchIfPassesPredicate('defaultTag', isLoading, {
    returns: () => <></>,
  }),
  addProps(({ defaultTag }) => ({
    defaultTag: hasData(defaultTag) ? defaultTag.data : null,
  })),
  addDataLoadingFromPromise(
    'data',
    ({
      username,
      brandId,
      tagId,
      labelId,
      initialFeedId,
      defaultTag,
      api,
      isAuthenticated,
    }) =>
      Promise.all([
        username
          ? api.users.feed(username)
          : brandId
          ? api.brands.feed(brandId)
          : tagId
          ? api.tags.feed(tagId)
          : labelId
          ? api.labels.feed(labelId)
          : initialFeedId === 'following'
          ? api.follows.feed()
          : initialFeedId === 'featured'
          ? api.users.featuredFeed()
          : defaultTag
          ? api.tags.feed(defaultTag.id)
          : api.users.featuredFeed(),
        api.tags.getAll(),
        isAuthenticated ? api.follows.creators() : Promise.resolve([]),
      ]),
    ['username', 'brandId', 'tagId', 'labelId', 'initialFeedId', 'defaultTag'],
    ({ username, brandId, tagId, labelId, initialFeedId, defaultTag }) => {
      const suffix = username
        ? `username-${username}`
        : brandId
        ? `brand-${brandId}`
        : tagId
        ? `tag-${tagId}`
        : labelId
        ? `label-${labelId}`
        : initialFeedId === 'following'
        ? 'following'
        : initialFeedId === 'featured'
        ? 'featured'
        : defaultTag
        ? `tag-${defaultTag}`
        : 'featured'
      return `feed-${suffix}`
    }
  ),
  addLoadingAndErrorIndicators('data', {
    redirectTo404IfError: true,
  }),
  addProps(({ data: [feed, tags, follows] }) => ({
    feed,
    tags,
    follows,
  })),
  addProps({
    rodeoOrEditView: typedAs<RodeoOrEditViewType>('rodeo'),
  }),
  addRodeoOrEditViewContextProvider,
  addTranslationHelpers,
  addProps(({ feed, initialFeedId, t }) => ({
    title: isUserFeed(feed)
      ? feed.user.username
      : isBrandFeed(feed)
      ? feed.brand.name
      : isTagFeed(feed)
      ? feed.tag.name
      : isLabelFeed(feed)
      ? feed.label.name
      : initialFeedId === 'following'
      ? t('feed.myCreatorsTitle')
      : t('feed.allTitle'),
  })),
  setPageTitle(({ title }) => title ?? null),
  addNavigationState(
    'currentFeedId',
    'setCurrentFeedId',
    ({ initialFeedId, tagId, defaultTag }) =>
      initialFeedId ?? tagId ?? defaultTag?.id ?? 'featured'
  ),
  addNavigate,
  addProps(({ follows, t }) => ({
    extraTabs: compact([
      {
        displayName: t('menu.all'),
        path: '/all',
        feedId: 'featured',
      },
      follows.length > 0
        ? {
            displayName: t('home.myCreatorsTab'),
            path: '/following',
            feedId: 'following',
          }
        : null,
    ]),
  })),
  addRef('scrollableContainerRef', typedAs<HTMLDivElement | null>(null)),
  addFeedContextProvider,
  addLocation,
  addNavigationState('showBackButton', 'setShowBackButton', false),
  addHandlers({
    onClickCurrentTab:
      ({ scrollableContainerRef }) =>
      () => {
        scrollableContainerRef.current?.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      },
    onClickBack:
      ({ navigate, username, visibleMediaId, location, showBackButton }) =>
      () => {
        if (showBackButton) {
          navBackOrHomepage()
        } else if (username) {
          navigate(makeCreatorProfileUrl(username, 'posts'), {
            state: {
              ...location.state,
              scrollTo: visibleMediaId,
            },
            replace: true,
          })
        }
      },
  }),
  addShowRegisterContext,
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    feed,
    currentFeedId,
    tags,
    extraTabs,
    onClickCurrentTab,
    onClickBack,
    scrollableContainerRef,
    showRegister,
    classes,
    t,
  }) => (
    <div ref={scrollableContainerRef} className={classes.container}>
      {!isUserFeed(feed) && !isBrandFeed(feed) && !isLabelFeed(feed) && (
        <ConstrainWidth className={classes.header}>
          <Tabs
            data={{ tags }}
            extraTabs={extraTabs}
            currentFeedId={currentFeedId}
            onClickCurrentTab={onClickCurrentTab}
          />
        </ConstrainWidth>
      )}
      {!isUserFeed(feed) && (
        <ConstrainWidth className={classes.navBarContainer}>
          {isBrandFeed(feed) ? (
            <NavBar title={feed.brand.name} showGradient />
          ) : isLabelFeed(feed) ? (
            <NavBar title={feed.label.name} showGradient />
          ) : (
            <NavBar
              CenterComponent={RodeoAndEditTabs}
              showProfileButton
              showGradient
              showRoundupButton
            />
          )}

          {currentFeedId !== 1 && (
            <Banner
              banner={{
                unDismissable: true,
                noHider: true,
                accessibleMenu: true,
                textTranslationKey: 'banners.mobileOpenDownloadApp.appName',
                onCloseAnalyticsEvent: {
                  options: {
                    category: 'general',
                    action: 'banner_open_download_in_app_close',
                  },
                },
                link: {
                  title: t('banners.mobileOpenDownloadApp.exclusiveTitle'),
                  navigateTo: window.location.href.replace(
                    `${window.location.protocol}//${window.location.hostname}${
                      window.location.port ? ':' + window.location.port : ''
                    }`,
                    GO2APP_URL.replace(/\/$/, '')
                  ),
                  external: false,
                  onClickAnalyticsEvent: {
                    options: {
                      category: 'general',
                      action: 'banner_open_download_in_app_click',
                    },
                  },
                },
              }}
              onAction={() => {}}
            />
          )}
        </ConstrainWidth>
      )}
      {isUserFeed(feed) && (
        <ConstrainWidth>
          <ImageButton
            image={backButtonIcon}
            width={20}
            height={20}
            onClick={onClickBack}
            className={classes.backButton}
          />
        </ConstrainWidth>
      )}
      <Fluid feed={feed} scrollableContainerRef={scrollableContainerRef} />
      {isUserFeed(feed) && !showRegister && (
        <ConstrainWidth>
          <Footer className={classes.wantMore} />
        </ConstrainWidth>
      )}
    </div>
  )
)
